"use client";

/**
 * The browser's record, on the pages that have it (#788 PR2): which page is
 * shown and for how long, which control is used, what fails on the screen —
 * and, until they have answered, the question whether they agree to it.
 *
 * Mounted once per layout ((app), (auth), (onboarding), (legal)), beside the
 * page rather than inside the navigation shell, and only where the server
 * takes the browser's record at all (BrowserRecord.tsx). It reads the
 * address and listens, and changes nothing about how a page is reached.
 * Nothing is sent unless their answer in this browser is yes
 * (record.ts `recording`).
 *
 * A control says what it is with a `data-track` attribute of
 * surface.action[.target]; one listener here, in the capture phase, reads it,
 * so a control needs no code of its own to be counted (tests/record.test.ts
 * holds every value in the tree to the shape the server takes).
 *
 * `/billing` and `/logout` are not recorded at all — not the page, not a
 * press on it, not an error (BLUEPRINT §21; review of PR2, blocking 3).
 *
 * Which page is in front of them, and since when, is the document's, not
 * this component's (record.ts `show`): there is one Recorder per layout, and
 * StrictMode mounts each twice. This one only says what the browser says —
 * the address, the tab hidden or shown again, the page going or coming back.
 */
import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import type { Lang } from "@/lib/i18n/index";
import { ConsentDialog } from "./ConsentDialog";
import { pageOf, UNRECORDED_PAGES } from "./routes";
import { actionOf, agreed, attach, hide, record, recording, restored, show, showing, unhide, wentBack } from "./record";

export type Choice = "granted" | "denied";

const surfaceOf = (route: string) => (route !== "/" ? route.split("/")[1] : "home");

export function Recorder({ consent, lang }: { consent: Choice | null; lang: Lang }) {
  // Set while this renders, not in an effect: the page's own components (a
  // row picked on arrival, the first search's state) record in their effects,
  // which run before this one's, and must find the answer already in place.
  const [choice, setChoice] = useState<Choice | null>(() => {
    recording(consent === "granted");
    return consent;
  });
  const [asked, setAsked] = useState(false);
  // The server's answer wins when it changes (a choice made on the settings
  // card comes back with the refresh it asks for).
  useEffect(() => setChoice(consent), [consent]);

  const pathname = usePathname();
  const search = useSearchParams();
  const route = pageOf(pathname);
  const recorded = route !== null && !UNRECORDED_PAGES.has(route);
  const stepRaw = route === "/onboarding" ? Number(search.get("step")) : NaN;
  const step = Number.isInteger(stepRaw) && stepRaw >= 1 && stepRaw <= 11 ? stepRaw : undefined;

  useEffect(() => {
    recording(choice === "granted");
    // Agreed while a page is open: that page is the first one told.
    if (choice === "granted") agreed();
  }, [choice]);

  useEffect(() => {
    show(recorded ? route : null, step);
  }, [recorded, route, step]);

  useEffect(() => {
    const detach = attach();
    const onVisibility = () => (document.visibilityState === "hidden" ? hide() : unhide());
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) restored(); };
    const onPop = () => wentBack();
    const onClick = (e: MouseEvent) => {
      if (!showing()) return;
      const el = (e.target as Element | null)?.closest?.("[data-track]");
      const action = actionOf(el?.getAttribute("data-track"));
      if (action) record("ui_action", action);
    };
    const onError = () => {
      const on = showing();
      if (on) record("client_error", { surface: surfaceOf(on), kind: "uncaught" });
    };
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("click", onClick, { capture: true });
    window.addEventListener("pagehide", hide);
    window.addEventListener("pageshow", onShow);
    window.addEventListener("popstate", onPop);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onError);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("pagehide", hide);
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onError);
      // The page is left, and what waits sent, once no layout's Recorder
      // shows it any more (record.ts `attach`).
      detach();
    };
  }, []);

  // Asked once, before anything else, wherever they first arrive — except on
  // the page that explains it, which has the same two answers on it, and the
  // pages that are not recorded at all. Put away without an answer, it is
  // asked again on the next load.
  const ask = choice === null && !asked && recorded && route !== "/privacy";
  return ask ? <ConsentDialog lang={lang} onAnswer={(c) => { setAsked(true); if (c) setChoice(c); }} /> : null;
}
